import { difference, differenceWith, uniqBy, sortBy } from 'lodash-es';
import type { DimensionNodeWithHierarchyContext } from '@bayer-int/portletce-shared';
import { DimensionNode } from '@bayer-int/portletce-shared';

import type { HierarchyFilterNode } from 'portlet/models';
import type { Dimension, Hierarchy } from 'portlet/services';

/**
 * @returns hierarchy filter node built with given dimension selection
 */
const dimensionNodeToHierarchyNode = (
    dimensionNode: DimensionNodeWithHierarchyContext,
): HierarchyFilterNode => ({
    identifier: dimensionNode.identifier,
    key: dimensionNode.key,
    value: dimensionNode.value,
    characteristicId: dimensionNode.characteristicId,
    internalKey: dimensionNode.internalKey,
    basedOnCharacteristicId: dimensionNode.basedOnCharacteristicId,
    basedOnInternalKey: dimensionNode.basedOnInternalKey,
    hierarchyId: dimensionNode.hierarchyId,
    keyPathToRoot: dimensionNode.keyPathToRoot,
    identifierPathToRoot: dimensionNode.identifierPathToRoot,
    valuePathToRoot: dimensionNode.valuePathToRoot,
});

/**
 * @returns whether filter hierarchy node is duplicate of selected dimension node
 */
export const areNodesEqual = (
    filterHierarchyElement: HierarchyFilterNode,
    dimensionNode: DimensionNode,
    internalKey: string,
): boolean =>
    filterHierarchyElement.internalKey === internalKey &&
    filterHierarchyElement.key === dimensionNode.key;

/**
 * @returns whether filter hierarchy node is contained in selected dimension node
 */
export const isDimensionNodeParentOf = (
    filterHierarchyElement: HierarchyFilterNode,
    dimensionNode: DimensionNode,
    hierarchyInternalKeys: Array<string>,
    dimensionInternalKey: string,
): boolean =>
    filterHierarchyElement.keyPathToRoot.some((key, idx) => {
        const internalKey = hierarchyInternalKeys[idx];

        return dimensionInternalKey === internalKey && dimensionNode.key === key;
    });

/**
 * @returns whether filter dimension node is contained in given filter hierarchy node
 */
export const isHierarchyNodeParentOf = (
    filterHierarchyElement: HierarchyFilterNode,
    dimensionNode: DimensionNodeWithHierarchyContext,
    hierarchyInternalKeys: Array<string>,
): boolean =>
    dimensionNode.keyPathToRoot.some((key, idx) => {
        const internalKey = hierarchyInternalKeys[idx];

        return (
            filterHierarchyElement.internalKey === internalKey && filterHierarchyElement.key === key
        );
    });

/**
 * @param currentHierarchyFilterNodes - current hierarchy filter nodes
 * @param selectedDimensionNodes - selected dimension nodes to merge
 * @param hierarchyInternalKeys - current hierarchy internal key structure
 * @param internalKey - selected dimension nodes internal key
 */
export const mergeFilterNodes = (
    currentHierarchyFilterNodes: Array<HierarchyFilterNode>,
    selectedDimensionNodes: Array<DimensionNode | HierarchyFilterNode>,
    hierarchy: Hierarchy,
    dimensionInternalKey: Dimension['internalKey'],
    options: {
        /** is set to false, nodes created from `selectedDimensionNodes` will have `filterUpstream` set to `false`  */
        applyFilterToUpstreamHierarchyLevels: boolean;
    },
): Array<HierarchyFilterNode> => {
    // remove nodes which do not have info about hierarchy they belong to
    const selectedDimensionNodesWithHierarchyContext: Array<
        DimensionNodeWithHierarchyContext | HierarchyFilterNode
    > = selectedDimensionNodes.filter(DimensionNode.hasHierarchyContext);

    /**
     * In case of real hierarchies, the list of filter nodes only contains
     * lowest selected items (parents are selected implicitly).
     * In virtual hierarchies, the hierarchy node list contains all selected nodes,
     * including ancestors of nodes selected by the user.
     */
    const mergedHierarchyFilterNodes =
        hierarchy.type === 'real'
            ? mergeRealHierarchy(
                  currentHierarchyFilterNodes,
                  selectedDimensionNodesWithHierarchyContext,
                  hierarchy,
                  dimensionInternalKey,
              )
            : mergeVirtualHierarchy(
                  currentHierarchyFilterNodes,
                  selectedDimensionNodesWithHierarchyContext,
                  hierarchy,
                  dimensionInternalKey,
              );

    return adjustFilterEffectInHierarchy(
        sortHierarchyFilterNodes(mergedHierarchyFilterNodes),
        selectedDimensionNodesWithHierarchyContext,
        currentHierarchyFilterNodes,
        hierarchy,
        options,
    );
};

function mergeRealHierarchy(
    currentHierarchyFilterNodes: Array<HierarchyFilterNode>,
    selectedDimensionNodesWithHierarchyContext: Array<DimensionNodeWithHierarchyContext>,
    hierarchy: Hierarchy,
    dimensionInternalKey: Dimension['internalKey'],
): Array<HierarchyFilterNode> {
    const dimensionDepth = hierarchy.internalKeys.indexOf(dimensionInternalKey);

    // removes hierarchy filter nodes that are contained in selected dimension nodes list of parent keys
    // e.g. removes Europe when selected France (dimension)
    const selectedNodes = differenceWith(
        currentHierarchyFilterNodes,
        selectedDimensionNodesWithHierarchyContext,
        (hierarchyNode, dimensionNode) =>
            isHierarchyNodeParentOf(hierarchyNode, dimensionNode, hierarchy.internalKeys),
    );

    // skips dimension nodes which are already 'covered' by a hierarchy node
    // (nodes are equal or dimension is a parent of the hierarchy node - and thus it should be not added again)
    // e.g. filters out Europe (dimension) if France is already selected
    const dimensionsToBeAdded = differenceWith(
        selectedDimensionNodesWithHierarchyContext,
        selectedNodes,
        (dimensionNode, hierarchyNode) =>
            areNodesEqual(hierarchyNode, dimensionNode, dimensionInternalKey) ||
            isDimensionNodeParentOf(
                hierarchyNode,
                dimensionNode,
                hierarchy.internalKeys,
                dimensionInternalKey,
            ),
    );

    // add dimension nodes if none exist on current depth
    const updatedNodes = [
        ...selectedNodes,
        ...dimensionsToBeAdded.map(dimensionNodeToHierarchyNode),
    ];

    const nodesBelowOrAtDimensionLevel = updatedNodes.filter(
        node => node.identifierPathToRoot.length >= dimensionDepth,
    );

    // remove hierarchy nodes which do not contain selected dimension node key in their parent keys
    // e.g. removes France, if selected Africa (dimension)
    const removedNodes = differenceWith(
        nodesBelowOrAtDimensionLevel,
        selectedDimensionNodesWithHierarchyContext,
        (hierarchyNode, dimensionNode) => {
            return (
                dimensionNode.keyPathToRoot.length > hierarchyNode.keyPathToRoot.length ||
                areNodesEqual(hierarchyNode, dimensionNode, dimensionInternalKey) ||
                isDimensionNodeParentOf(
                    hierarchyNode,
                    dimensionNode,
                    hierarchy.internalKeys,
                    dimensionInternalKey,
                )
            );
        },
    );

    return difference(updatedNodes, removedNodes);
}

function mergeVirtualHierarchy(
    currentHierarchyFilterNodes: Array<HierarchyFilterNode>,
    selectedDimensionNodesWithHierarchyContext: Array<DimensionNodeWithHierarchyContext>,
    hierarchy: Hierarchy,
    dimensionInternalKey: Dimension['internalKey'],
): Array<HierarchyFilterNode> {
    const currentHierarchyLowestFilterNodes = filterOutAncestorsOfSelectedHierarchyNodes(
        currentHierarchyFilterNodes,
    );

    const merged = mergeRealHierarchy(
        currentHierarchyLowestFilterNodes,
        selectedDimensionNodesWithHierarchyContext,
        hierarchy,
        dimensionInternalKey,
    );

    return addAncestorsOfSelectedHierarchyNodes(merged, hierarchy);
}

function filterOutAncestorsOfSelectedHierarchyNodes(
    nodes: Array<HierarchyFilterNode>,
): Array<HierarchyFilterNode> {
    const allParentIdentifiers = nodes.flatMap(node => node.identifierPathToRoot);

    return nodes.filter(node => !allParentIdentifiers.includes(node.identifier));
}

export function addAncestorsOfSelectedHierarchyNodes(
    nodes: Array<HierarchyFilterNode>,
    hierarchy: Hierarchy,
): Array<HierarchyFilterNode> {
    const nodesWithAncestors = nodes.flatMap(node => {
        const parents = node.identifierPathToRoot.map(
            (identifier, idx): HierarchyFilterNode => ({
                identifier,
                key: node.keyPathToRoot[idx],
                value: node.valuePathToRoot[idx],
                hierarchyId: node.hierarchyId,
                internalKey: hierarchy.internalKeys[idx] ?? node.internalKey,
                characteristicId: hierarchy.characteristicIds[idx] ?? node.characteristicId,
                basedOnInternalKey: hierarchy.basedOnInternalKeys[idx] ?? node.basedOnInternalKey,
                basedOnCharacteristicId:
                    hierarchy.basedOnCharacteristicIds[idx] ?? node.basedOnCharacteristicId,
                identifierPathToRoot: node.identifierPathToRoot.slice(0, idx),
                keyPathToRoot: node.keyPathToRoot.slice(0, idx),
                valuePathToRoot: node.valuePathToRoot.slice(0, idx),
            }),
        );

        return [...parents, node];
    });

    return uniqBy(nodesWithAncestors, ({ identifier }) => identifier);
}

/**
 * Depending on circumstances, we may want to modify how the filter node should be handled.
 * For example, for certain portlets we may want to keep the node inside the filter (to e.g. ensure consistency with hierarchy selector)
 * but ignore the node when building bw query. This is achieved by setting additional property `effect`.
 *
 * Introduced in 3167: Hierarchy filtering - don't autoselect upstream
 */
export function adjustFilterEffectInHierarchy(
    nodes: Array<HierarchyFilterNode>,
    added: Array<DimensionNodeWithHierarchyContext | HierarchyFilterNode>,
    previousNodes: Array<HierarchyFilterNode>,
    hierarchy: Hierarchy,
    options: {
        /** is set to false, nodes created from `selectedDimensionNodes` will have `effect` set to `upstream-disable`  */
        applyFilterToUpstreamHierarchyLevels: boolean;
    },
): Array<HierarchyFilterNode> {
    return nodes.map(node => {
        const previous = previousNodes.find(({ identifier }) => node.identifier === identifier);
        const hasBeenSelected = added.some(({ identifier }) => identifier === node.identifier);
        const isParentOfSelected = added.some(dimension =>
            isHierarchyNodeParentOf(node, dimension, hierarchy.internalKeys),
        );

        // if not subject of change, return as is
        // returning `previous` in case it exists preserves `effect` (and potentially other extra properties)
        if (!hasBeenSelected && !isParentOfSelected) return previous || node;

        // for the selected node respect the `applyFilterToUpstreamHierarchyLevels` setting
        if (hasBeenSelected)
            return {
                ...node,
                effect: options.applyFilterToUpstreamHierarchyLevels
                    ? 'active'
                    : 'upstream-disable',
            };

        // this is a non-unobvious case:
        // we want to persist existing `active` parent nodes, so that if `applyFilterToUpstreamHierarchyLevels`
        // is `false` the parent node will not become `disabled`
        if (
            isParentOfSelected &&
            previous &&
            (previous.effect === 'active' || previous.effect === undefined)
        )
            return previous;

        // for parents of selected node respect the `applyFilterToUpstreamHierarchyLevels` setting
        if (isParentOfSelected)
            return {
                ...node,
                effect: options.applyFilterToUpstreamHierarchyLevels ? 'active' : 'disabled',
            };

        return previous || node;
    });
}

/**
 * Performs depth first sorting by text
 */
function sortHierarchyFilterNodes(nodes: Array<HierarchyFilterNode>): Array<HierarchyFilterNode> {
    return sortBy(nodes, ({ valuePathToRoot, value }) => [...valuePathToRoot, value].join('-'));
}
